import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import store from './store';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Homepage from './components/Homepage';
import AIFleetPage from './components/AIFleetPage';
import AIFleetModalPage from './components/AIFleetModalPage';
import ProductDetailPage from './components/ProductDetailPage';
import OrderConfirmationPage from './components/OrderConfirmationPage';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import OrderHistoryPage from './components/OrderHistoryPage';
import SupportPage from './components/SupportPage';
import ChatPage from './components/ChatPage';
import ComingSoonPage from './components/ComingSoonPage';
import ListRedirectPage from './components/ListRedirectPage';
import SettingsPage from './components/SettingsPage';
import ProductsPage from './components/ProductsPage';
import Shop from './pages/Shop';
import ShopCheckout from './pages/ShopCheckout';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import SubscriptionTerms from './pages/SubscriptionTerms';
import Disclaimer from './pages/Disclaimer';
import EmailInbox from './components/EmailInbox';
import SelfServiceRenewal from './pages/SelfServiceRenewal';
import ChangePassword from './pages/ChangePassword';
import CardActivation from './pages/CardActivation';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { Menu } from 'lucide-react';
import ScrollToTop from './components/ScrollToTop';
import PodcastPage from './components/PodcastPage';
import PodcastSidebar from './components/PodcastSidebar';
import FileManagerPage from './components/FileManagerPage';
import ChatGPTPlus from './pages/ChatGPTPlus';
import ChatGPTPlusRenewal from './pages/ChatGPTPlusRenewal';
import ProductDetail from './pages/ProductDetail';
import AIToolboxPage from './components/AIToolboxPage';
import AIVideoPage from './components/AIVideoPage';
import AIPPTPage from './components/AIPPTPage';
import GoogleScholarPage from './components/GoogleScholarPage';
import PricingPage from './components/PricingPage';

// PaymentReceivedWrapper 组件 - 将路径参数转换为查询参数
function PaymentReceivedWrapper() {
  const { orderId } = useParams();
  const [isReady, setIsReady] = React.useState(false);
  
  React.useEffect(() => {
    if (orderId) {
      // 获取当前的查询参数
      const searchParams = new URLSearchParams(window.location.search);
      
      // 如果URL中没有orderId查询参数，添加它
      if (!searchParams.has('orderId')) {
        searchParams.set('orderId', orderId);
        // 更新URL但不触发页面重新加载
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
      }
    }
    
    // 设置为准备就绪，这样PaymentSuccessPage就能正确获取到orderId
    setIsReady(true);
  }, [orderId]);
  
  // 只有在URL更新完成后才渲染PaymentSuccessPage
  if (!isReady) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-2 border-ink-200 border-t-accent-500 mx-auto mb-4"></div>
          <p className="text-ink-600">加载中...</p>
        </div>
      </div>
    );
  }
  
  return <PaymentSuccessPage />;
}

// 播客专用布局组件
function PodcastLayout({ children }) {
  const { isCollapsed, isMobile } = useSidebar();

  return (
    <div className="App">
      <PodcastSidebar />
      <main className={`${isMobile ? '' : isCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`}>
        {children}
      </main>
    </div>
  );
}

// 简单的布局组件
function Layout({ children }) {
  const location = useLocation();
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar();

  // 固定的侧边栏页面列表
  const sidebarPages = [
    '/',
    '/ai-fleet',
    '/ai-chat/sidebar',
    '/ai-image/sidebar',
    '/ai-video/sidebar',
    '/ai-toolbox/sidebar',
    '/google-scholar/sidebar',
    '/list',
    '/orders-sidebar',
    '/support-sidebar',
    '/settings',
    '/payment-success-sidebar',
    '/shop',
    '/purchase',
    '/change-password',
    '/card-activation'
  ];

  // 无导航栏页面列表（既不显示顶栏也不显示侧边栏）
  const noNavigationPages = [
    '/detail/info',
    '/detail/success'
  ];

  // 判断是否显示侧边栏
  const shouldShowSidebar = sidebarPages.some(page => {
    if (page === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(page);
  });

  // 判断是否为无导航栏页面
  const shouldHideNavigation = noNavigationPages.some(page =>
    location.pathname.startsWith(page)
  );

  // 计算 main 的 margin：移动端不需要 margin，桌面端根据侧边栏状态设置
  const mainClassName = shouldHideNavigation
    ? ''
    : shouldShowSidebar
      ? isMobile
        ? '' // 移动端内容占满全宽
        : `${isCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`
      : '';

  return (
    <div className="App">
      {!shouldHideNavigation && (shouldShowSidebar ? <Sidebar /> : <Header />)}

      {/* 移动端顶部汉堡按钮 */}
      {!shouldHideNavigation && shouldShowSidebar && isMobile && (
        <div className="fixed top-0 left-0 right-0 z-20 bg-cream-50/90 backdrop-blur-md h-14 flex items-center px-4 border-b border-ink-200">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md text-ink-700 hover:bg-cream-100 transition-colors duration-200"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="ml-3 flex items-center gap-2">
            <img
              src="/images/logo192.png"
              alt="Logo"
              className="w-8 h-8 rounded-lg border border-ink-200"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-ink-900 font-semibold text-base">紫刀AI</span>
          </Link>
        </div>
      )}

      <main className={`${mainClassName} ${
        !shouldHideNavigation && shouldShowSidebar && isMobile ? 'pt-14' : ''
      }`}>
        {children}
      </main>

      {!shouldShowSidebar && !shouldHideNavigation && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <ScrollToTop />
        <SidebarProvider>
          <Layout>
            <Routes>
              {/* 首页 - 显示AI车队页面 */}
              <Route path="/" element={<AIFleetPage />} />
              
              {/* ChatGPT Plus页面 */}
              <Route path="/chatgpt-plus" element={<ChatGPTPlus />} />
              
              {/* 原始营销首页 */}
              <Route path="/homepage" element={<Homepage />} />
              
              {/* 产品页面 */}
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/product/:productId" element={<ProductDetailPage />} />
              
              {/* 订单相关 */}
              <Route path="/detail/info" element={<OrderConfirmationPage />} />
              <Route path="/detail/success" element={<PaymentSuccessPage />} />
              <Route path="/orders" element={<OrderHistoryPage />} />
              
              {/* 支付成功页面路由 - 侧边栏版本 */}
              <Route path="/payment-success-sidebar" element={<PaymentSuccessPage />} />
              
              {/* 支付收款页面路由 - 使用路径参数传递orderId */}
              <Route path="/payment-received/:orderId" element={<PaymentReceivedWrapper />} />
              
              {/* 购买记录页面路由 - 侧边栏版本 */}
              <Route path="/orders-sidebar" element={<OrderHistoryPage />} />
              
              {/* 支持页面路由 - 侧边栏版本 */}
              <Route path="/support-sidebar" element={<SupportPage />} />
              
              {/* AI车队页面 */}
              <Route path="/ai-fleet" element={<AIFleetPage />} />
              {/* AI车队带弹窗页面 - 测试用 */}
              <Route path="/ai-fleet/gpt-backup-modal" element={<AIFleetModalPage />} />
              <Route path="/ai-fleet/:fleetType" element={<AIFleetPage />} />
              
              {/* AI Chat页面路由 - 暂时显示即将推出页面 */}
              <Route path="/ai-chat/sidebar" element={<ComingSoonPage feature="AI Chat" description="智能对话功能正在开发中，即将为您带来更强大的AI助手体验。" />} />
              
              {/* AI Image页面路由 - 暂时显示即将推出页面 */}
              <Route path="/ai-image/sidebar" element={<ComingSoonPage feature="AI Image" description="AI图像生成功能正在开发中，即将为您带来创意无限的图像创作体验。" />} />
              <Route path="/ai-image/sidebar/workspace" element={<ComingSoonPage feature="AI Image" description="AI图像生成功能正在开发中，即将为您带来创意无限的图像创作体验。" />} />
              <Route path="/ai-image/sidebar/my-creations" element={<ComingSoonPage feature="AI Image" description="AI图像生成功能正在开发中，即将为您带来创意无限的图像创作体验。" />} />
              
              {/* AI Video页面路由 */}
              <Route path="/ai-video/sidebar" element={<AIVideoPage />} />

              {/* AI工具箱页面路由 */}
              <Route path="/ai-toolbox/sidebar" element={<AIToolboxPage />} />
              <Route path="/ai-toolbox/sidebar/aippt" element={<AIPPTPage />} />
              
              {/* 谷歌学术页面路由 */}
              <Route path="/google-scholar/sidebar" element={<GoogleScholarPage />} />
              
              {/* List页面路由 - 支持哈希路径重定向到AI Fleet */}
              <Route path="/list" element={<ListRedirectPage />} />
              <Route path="/list/*" element={<ListRedirectPage />} />
              
              {/* 其他页面 */}
              <Route path="/support" element={<SupportPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/settings" element={<SettingsPage />} />

              {/* 购买页面 - 使用左右分栏布局 */}
              <Route path="/shop" element={<ShopCheckout />} />

              {/* 新版购买页面 - 左右分栏布局 */}
              <Route path="/purchase" element={<ShopCheckout />} />

              {/* 老版商城页面 - 备份 */}
              <Route path="/shop-old" element={<Shop />} />

              {/* 邮箱查询页面路由 */}
              <Route path="/email-inbox" element={<EmailInbox />} />
              
              {/* 自助续费页面路由 */}
              <Route path="/renewal" element={<SelfServiceRenewal />} />
              
              {/* 修改密码页面路由 */}
              <Route path="/change-password" element={<ChangePassword />} />
              
              {/* 卡密激活页面路由 */}
              <Route path="/card-activation" element={<CardActivation />} />
              
              {/* 隐藏的播客功能路由 - 不在导航中显示 */}
              <Route path="/podcast/sidebar" element={<PodcastLayout><PodcastPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/file-manager" element={<PodcastLayout><FileManagerPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/uploads" element={<PodcastLayout><FileManagerPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/audio-library" element={<PodcastLayout><FileManagerPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/projects" element={<PodcastLayout><FileManagerPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/recorder" element={<PodcastLayout><PodcastPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/editor" element={<PodcastLayout><PodcastPage /></PodcastLayout>} />
              <Route path="/podcast/sidebar/project-manager" element={<PodcastLayout><PodcastPage /></PodcastLayout>} />
              
              {/* ChatGPT Plus续订页面 */}
              <Route path="/chatgpt-plus-renewal" element={<ChatGPTPlusRenewal />} />
              
              {/* 产品详情页面 */}
              <Route path="/product-detail" element={<ProductDetail />} />
              
              {/* 价格页面 */}
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* 法律页面 */}
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/subscription" element={<SubscriptionTerms />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              
              {/* 即将推出的功能 */}
              <Route path="/ai-chat" element={<ComingSoonPage feature="AI Chat" />} />
              <Route path="/ai-image" element={<ComingSoonPage feature="AI Image" />} />
              
              {/* 404页面 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
        </SidebarProvider>
      </Router>
    </Provider>
  );
}

// 404页面组件 - 使用国际化
function NotFoundPage() {
  const { t } = useTranslation();
  
  return (
    <div className="container-page py-20 text-center">
      <h1 className="text-4xl font-bold text-ink-900 mb-4">
        {t('errors.pageNotFound', '页面不存在')}
      </h1>
      <p className="text-gray-600">
        {t('errors.pageNotFoundDesc', '抱歉，您访问的页面不存在。')}
      </p>
      <a href="/" className="mt-4 inline-block text-blue-600 hover:underline">
        {t('navigation.home')}
      </a>
    </div>
  );
}

export default App;
